import { DataTypeProvider } from '@devexpress/dx-react-grid';
import Box from '@mui/material/Box';
import { CONSTANTS } from '../../../constants/constants';
import { useContext } from 'react';
import { FiltersContext } from '../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import { DrawerStackContext } from '../../NRResizableDrawerStack/DrawerStackContext';
import Typography from '@mui/material/Typography';
import NRValueTooltipIcon from '../../NRReactGridCustomComponents/NRCustomProviders/NRValueTooltipIcon';
import { useTheme } from '@mui/material/styles';
import NRKEVTwoToneIcon from '../../../NRIcons/NRKEVTwoToneIcon';
import NRVulnerabilityDetails from '../NRVulnerabilityDetails/NRVulnerabilityDetails';

const { DETAIL } = CONSTANTS.TABLE.VULNERABILITIES_TABLE.VULNERABILITY_DETAIL_DRAWER;

const KEV_TOOLTIP =
  'This CVE appears in CISA’s Known Exploited Vulnerabilities (KEV) Catalog, more info related to this KEV is available in the CVE details.';

function NRCVEIdFormatter({ value, column, row }) {
  const filtersCtx = useContext(FiltersContext);
  const drawerStack = useContext(DrawerStackContext);

  const theme = useTheme();
  const { statusPalette } = theme.palette;
  const { inKnownExploitedVulnerabilities } = row;

  const canOpenLegacyDrawer =
    !!filtersCtx &&
    typeof filtersCtx.setOpenDrawer === 'function' &&
    typeof filtersCtx.setDrawerContent === 'function' &&
    typeof filtersCtx.setDrawerTitle === 'function' &&
    Array.isArray(filtersCtx.drawerContent);

  // Clickable when either the resizable drawer stack or the legacy shared drawer is available.
  const canOpenDrawer = !!drawerStack || canOpenLegacyDrawer;

  const handleOpenDrawer = () => {
    const details = <NRVulnerabilityDetails key={`vuln-details-${row?.cve ?? value}`} row={row} />;

    // Prefer the resizable drawer stack (push a new page with working Back), else legacy shared drawer.
    if (drawerStack) {
      drawerStack.pushDrawer({ title: `${row.cve} ${DETAIL}`, content: details });
      return;
    }

    if (!canOpenLegacyDrawer) return;

    const { setOpenDrawer, setDrawerContent, drawerContent, setDrawerTitle, drawerTitle, setHasBack } = filtersCtx;

    if (typeof setHasBack === 'function' && !!drawerContent.length) setHasBack(true);

    const nextTitleStack = Array.isArray(drawerTitle) ? drawerTitle : [];
    setDrawerTitle([...nextTitleStack, `${row.cve} ${DETAIL}`]);

    setDrawerContent([...drawerContent, <NRVulnerabilityDetails key={`vuln-details-${row?.cve ?? value}-${drawerContent.length}`} row={row} />]);

    setOpenDrawer(true);
  };


  const handleKeyDown = event => {
    if (!canOpenDrawer) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenDrawer();
    }
  };

  const icon = inKnownExploitedVulnerabilities ? <NRKEVTwoToneIcon width={20} height={20} color={statusPalette[1]} /> : null;
  const tooltip = inKnownExploitedVulnerabilities ? KEV_TOOLTIP : null;

  const containerSx = {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    px: 2,
    py: 1.5,
    width: '100%',
    height: '100%'
  };

  if (!canOpenDrawer) {
    return (
      <Box sx={containerSx}>
        <NRValueTooltipIcon
          style={{ width: '100%' }}
          value={
            <Typography variant="body2" color={theme.palette.text.secondary}>
              {value}
            </Typography>
          }
          icon={icon}
          tooltip={tooltip}
        />
      </Box>
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Open details drawer for CVE ${value}`}
      sx={{
        ...containerSx,
        cursor: 'pointer',
        '&:focus-visible': {
          outline: '2px solid',
          outlineOffset: '2px',
          outlineColor: theme.palette.primary.main
        }
      }}
      onClick={handleOpenDrawer}
      onKeyDown={handleKeyDown}
    >
      <NRValueTooltipIcon
        style={{ width: '100%' }}
        value={
          <Typography variant="body2" color={theme.palette.text.secondary} sx={{ textDecoration: 'underline' }}>
            {value}
          </Typography>
        }
        icon={icon}
        tooltip={tooltip}
      />
    </Box>
  );
}

export const NRCVEIdProvider = props => <DataTypeProvider formatterComponent={NRCVEIdFormatter} {...props} />;
